/**
 * Hover (or keyboard focus) on a board row's "Today's list" cell (19/10/2026):
 * every list of today's set for that row in the order it is SENT, every
 * list group together (26/10/2026) —
 * "05:00 🇩🇰 · AU_ACE_FTD_LAST30", ×N when a list goes out several times,
 * then the skipped/stopped and not-planned ones. Radix Tooltip, as the app's
 * other hover cards: it flips and caps its height at the screen edge, the list
 * scrolls. No aria-label on the content, so a screen reader hears every line.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactElement } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';

import type { ClockCtx } from '@/features/drop-generator/utils/clock/dk-label';

import { listName, sendTimeLabel, type TodayListEntry } from './today-list-order';

interface Props {
  /** The group's name, or "N groups" when the row has several. */
  label: string;
  entries: TodayListEntry[];
  clock: ClockCtx;
  children: ReactElement;
}

const titleOf = (label: string, n: number) => (n === 1 ? `${label} · today's list` : `${label} · ${n} lists today, in send order`);

// One grid row per list (the <li> is display: contents) so the names line up after a "15:00 −1d" time.
const Line: FC<{ entry: TodayListEntry; clock: ClockCtx }> = ({ entry, clock }) => (
  <li className="contents" data-testid="today-list-line">
    {/* Spaces between the parts keep the line readable when copied or read aloud; the grid ignores them. */}
    <span className="opacity-70">{entry.minute === null ? '—' : sendTimeLabel(entry.minute, clock)}</span>{' '}
    <span className="opacity-50">·</span>{' '}
    <span className="min-w-0 truncate" title={listName(entry)}>{listName(entry)}</span>{' '}
    <span className="justify-self-end">
      {entry.sends > 1 ? <span className="rounded bg-background/15 px-1 text-[10px] font-semibold">×{entry.sends}</span> : null}
      {entry.minute === null ? <span className="font-sans text-[10px] italic opacity-60">{entry.off ?? 'not planned'}</span> : null}
    </span>
  </li>
);

// Portal and Content together: the Portal's Presence needs a ref-forwarding child.
const Card: FC<Omit<Props, 'children'>> = ({ label, entries, clock }) => (
  <RadixTooltip.Portal>
    <RadixTooltip.Content
      side="bottom"
      align="start"
      sideOffset={6}
      avoidCollisions
      collisionPadding={12}
      className="z-[200] flex max-h-[min(40rem,var(--radix-tooltip-content-available-height))] w-max min-w-[220px] max-w-[min(32rem,calc(100vw-1.5rem))] flex-col gap-1.5 rounded-md bg-foreground px-3 py-2.5 text-background shadow-lg"
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{titleOf(label, entries.length)}</p>
      <ol
        className="grid min-h-0 grid-cols-[max-content_max-content_minmax(0,1fr)_max-content] items-baseline gap-x-1.5 overflow-y-auto overscroll-contain pr-1 font-mono text-[11px] leading-5 tabular-nums"
        aria-label="Today's lists in send order"
      >
        {entries.map((entry) => <Line key={entry.wiserId} entry={entry} clock={clock} />)}
      </ol>
    </RadixTooltip.Content>
  </RadixTooltip.Portal>
);

export const TodayListsHoverCard: FC<Props> = ({ children, ...card }) => {
  if (!card.entries.length) return children;
  return (
    <RadixTooltip.Provider delayDuration={150} skipDelayDuration={80}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <Card {...card} />
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};
